// ═══════════════════════════════════════════════════════════
// Workflows API
//
// Workflows are reusable templates of ordered task steps.
// Applying a workflow to a request auto-creates tasks for each
// step with calculated due dates based on the request deadline.
//
// Workflow → WorkflowSteps (ordered by stepOrder)
// Apply → creates Tasks on the request record
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { createTask, type TaskType } from './tasks';

const FW = FIELDS.workflows;
const FS = FIELDS.workflowSteps;

// ── Types ────────────────────────────────────────────────

export interface Workflow {
  recordId:       number;
  jurisdictionId: string;
  name:           string;
  description?:   string;
  isActive:       boolean;
  isDefault:      boolean;
}

export interface WorkflowStep {
  recordId:        number;
  workflowId:      number;
  stepOrder:       number;
  name:            string;
  taskType:        TaskType;
  assignedRole?:   string;
  daysToComplete:  number;
  isClockPausing:  boolean;
  requiresApproval:boolean;
}

export interface CreateWorkflowInput {
  jurisdictionId: string;
  name:           string;
  description?:   string;
  isActive?:      boolean;
  isDefault?:     boolean;
}

export interface CreateWorkflowStepInput {
  workflowId:      number;
  stepOrder:       number;
  name:            string;
  taskType:        TaskType;
  assignedRole?:   string;
  daysToComplete?: number;
  isClockPausing?: boolean;
  requiresApproval?:boolean;
}

// ── Row mappers ──────────────────────────────────────────

function rowToWorkflow(row: Record<string, unknown>): Workflow {
  return {
    recordId:       row['record_id'] as number,
    jurisdictionId: String(row[FW.jurisdictionId] ?? ''),
    name:           String(row[FW.name] ?? ''),
    description:    row[FW.description] as string | undefined,
    isActive:       row[FW.isActive] === true || row[FW.isActive] === 'true',
    isDefault:      row[FW.isDefault] === true || row[FW.isDefault] === 'true',
  };
}

function rowToStep(row: Record<string, unknown>): WorkflowStep {
  return {
    recordId:         row['record_id'] as number,
    workflowId:       Number(row[FS.workflowId] ?? 0),
    stepOrder:        Number(row[FS.stepOrder] ?? 0),
    name:             String(row[FS.name] ?? ''),
    taskType:         (row[FS.taskType] as TaskType) ?? 'other',
    assignedRole:     row[FS.assignedRole] as string | undefined,
    daysToComplete:   Number(row[FS.daysToComplete] ?? 3),
    isClockPausing:   row[FS.isClockPausing] === true || row[FS.isClockPausing] === 'true',
    requiresApproval: row[FS.requiresApproval] === true || row[FS.requiresApproval] === 'true',
  };
}

// ── Workflows ────────────────────────────────────────────

export async function listWorkflows(jurisdictionId: string): Promise<Workflow[]> {
  const { records } = await queryRecords(TABLES.workflows, {
    filters: [{ field: FW.jurisdictionId, operator: 'eq', value: jurisdictionId }],
    sort: { field: FW.name, direction: 'asc' },
    limit: 100,
  });
  return (records as Record<string, unknown>[]).map(rowToWorkflow);
}

export async function createWorkflow(input: CreateWorkflowInput): Promise<Workflow> {
  const row = await createRecord(TABLES.workflows, {
    [FW.jurisdictionId]: input.jurisdictionId,
    [FW.name]:           input.name,
    [FW.description]:    input.description ?? '',
    [FW.isActive]:       input.isActive ?? true,
    [FW.isDefault]:      input.isDefault ?? false,
  });
  return rowToWorkflow(row as Record<string, unknown>);
}

export async function updateWorkflow(
  recordId: number,
  input: Partial<CreateWorkflowInput>,
): Promise<Workflow> {
  const patch: Record<string, unknown> = {};
  if (input.name        !== undefined) patch[FW.name]        = input.name;
  if (input.description !== undefined) patch[FW.description] = input.description;
  if (input.isActive    !== undefined) patch[FW.isActive]    = input.isActive;
  if (input.isDefault   !== undefined) patch[FW.isDefault]   = input.isDefault;
  const row = await updateRecord(TABLES.workflows, recordId, patch);
  return rowToWorkflow(row as Record<string, unknown>);
}

// ── Workflow Steps ───────────────────────────────────────

export async function listWorkflowSteps(workflowId: number): Promise<WorkflowStep[]> {
  const { records } = await queryRecords(TABLES.workflowSteps, {
    filters: [{ field: FS.workflowId, operator: 'eq', value: workflowId }],
    sort: { field: FS.stepOrder, direction: 'asc' },
    limit: 50,
  });
  return (records as Record<string, unknown>[]).map(rowToStep);
}

export async function createWorkflowStep(input: CreateWorkflowStepInput): Promise<WorkflowStep> {
  const row = await createRecord(TABLES.workflowSteps, {
    [FS.workflowId]:       input.workflowId,
    [FS.stepOrder]:        input.stepOrder,
    [FS.name]:             input.name,
    [FS.taskType]:         input.taskType,
    [FS.assignedRole]:     input.assignedRole ?? '',
    [FS.daysToComplete]:   input.daysToComplete ?? 3,
    [FS.isClockPausing]:   input.isClockPausing ?? false,
    [FS.requiresApproval]: input.requiresApproval ?? false,
  });
  return rowToStep(row as Record<string, unknown>);
}

export async function updateWorkflowStep(
  recordId: number,
  input: Partial<CreateWorkflowStepInput>,
): Promise<WorkflowStep> {
  const patch: Record<string, unknown> = {};
  if (input.stepOrder        !== undefined) patch[FS.stepOrder]        = input.stepOrder;
  if (input.name             !== undefined) patch[FS.name]             = input.name;
  if (input.taskType         !== undefined) patch[FS.taskType]         = input.taskType;
  if (input.assignedRole     !== undefined) patch[FS.assignedRole]     = input.assignedRole;
  if (input.daysToComplete   !== undefined) patch[FS.daysToComplete]   = input.daysToComplete;
  if (input.isClockPausing   !== undefined) patch[FS.isClockPausing]   = input.isClockPausing;
  if (input.requiresApproval !== undefined) patch[FS.requiresApproval] = input.requiresApproval;
  const row = await updateRecord(TABLES.workflowSteps, recordId, patch);
  return rowToStep(row as Record<string, unknown>);
}

export async function deleteWorkflowStep(recordId: number): Promise<void> {
  await deleteRecord(TABLES.workflowSteps, recordId);
}

export async function deleteWorkflow(recordId: number): Promise<void> {
  // Delete all steps first
  const steps = await listWorkflowSteps(recordId);
  await Promise.all(steps.map(s => deleteRecord(TABLES.workflowSteps, s.recordId)));
  await deleteRecord(TABLES.workflows, recordId);
}

// ── Apply to Request ─────────────────────────────────────

/**
 * Apply a workflow to a request by creating a task for each step.
 *
 * Due dates are calculated forward from today using each step's
 * daysToComplete. If the request has a deadline, the final step
 * is pinned to it and earlier steps work backwards.
 *
 * Returns the created tasks.
 */
export async function applyWorkflowToRequest(
  workflowId: number,
  requestId: number,
  actorEmail: string,
  opts?: { deadlineDate?: string },
): Promise<void> {
  const steps = await listWorkflowSteps(workflowId);
  if (steps.length === 0) return;

  const totalDays = steps.reduce((sum, s) => sum + s.daysToComplete, 0);
  const anchor = opts?.deadlineDate ? new Date(opts.deadlineDate) : null;

  // Calculate due date for each step:
  // If we have a deadline, pin the last step to it and work backwards.
  // Otherwise, work forward from today.
  const dueDates: string[] = [];

  if (anchor) {
    // Work backwards from deadline
    let cursor = new Date(anchor);
    for (let i = steps.length - 1; i >= 0; i--) {
      dueDates[i] = cursor.toISOString().split('T')[0];
      cursor.setDate(cursor.getDate() - steps[i].daysToComplete);
    }
  } else {
    // Work forwards from today
    let cursor = new Date();
    for (let i = 0; i < steps.length; i++) {
      cursor.setDate(cursor.getDate() + steps[i].daysToComplete);
      dueDates[i] = cursor.toISOString().split('T')[0];
    }
  }

  // Create tasks in order (parallel — order is tracked by task type + title, not creation order)
  await Promise.all(steps.map((step, i) =>
    createTask({
      requestId,
      type:        step.taskType,
      title:       step.name,
      description: step.requiresApproval ? 'Requires approval before proceeding.' : undefined,
      dueDate:     dueDates[i],
      isBlocking:  step.requiresApproval,
    }, actorEmail)
  ));
}
